import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Code, Monitor, Smartphone, Globe, Rocket, 
  CheckCircle, Briefcase, BarChart, 
  Cpu, Database, Server, Layers, ExternalLink,
  Menu, Zap, X, Send
} from 'lucide-react';

// --- SUCCESS POPUP (Matching WithApp Design) ---
const SuccessPopup = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[70] flex justify-center items-center px-4">
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={onClose}
          />
          {/* Popup Card */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="bg-white rounded-2xl p-8 w-full max-w-sm text-center shadow-2xl relative z-10"
          >
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-200">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Request Sent!</h3>
            <p className="text-slate-500 mb-6 text-sm">
              We have received your details. Our tech team will connect with you shortly.
            </p>
            <button 
              onClick={onClose} 
              className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-xl font-semibold transition shadow-lg"
            >
              Close
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const WithWeb: React.FC = () => {
  // --- STATE FOR MODAL FORM & BACKEND ---
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form Data State
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    phone: '',
    email: '',
    projectBrief: ''
  });

  // --- HANDLERS ---
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = { ...formData, serviceType: "Web Development" };

    // Backend Call
    fetch('https://www.projectinfotech.in/api/quotation/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    }).catch(err => console.error("Background submission error:", err));

    // Show Success
    setTimeout(() => {
        setIsSubmitting(false);
        setFormData({ name: '', companyName: '', phone: '', email: '', projectBrief: '' });
        setIsFormOpen(false);
        setShowSuccess(true);
    }, 800);
  };

  // --- ANIMATION VARIANTS ---
  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    visible: { transition: { staggerChildren: 0.2 } }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans overflow-x-hidden selection:bg-red-500 selection:text-white">
      
      {/* ================= HERO SECTION ================= */}
      <section className="relative w-full pt-32 pb-24 px-6 bg-[#0B0F19] text-white overflow-hidden">
        {/* Background Glows */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-red-600/20 blur-[150px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-blue-600/10 blur-[150px] rounded-full pointer-events-none" />

        <div className="container mx-auto max-w-7xl relative z-10 flex flex-col lg:flex-row items-center gap-16">
          
          {/* LEFT CONTENT */}
          <motion.div 
            initial="hidden" animate="visible" variants={fadeIn}
            className="lg:w-1/2 space-y-8"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs font-bold tracking-widest text-slate-300 uppercase">PIT - Official Website Partner</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight tracking-tight font-aligran">
              Turn Your Ideas Into <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-400">
                Digital Reality
              </span>
            </h1>
            
            <p className="text-lg text-slate-400 max-w-xl leading-relaxed">
              We are a full-stack development agency & training institute. 
              From <strong>B2B Platforms</strong> to <strong>Luxury Brand Websites</strong>, 
              we build solutions that scale.
            </p>

            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => setIsFormOpen(true)}
                className="px-8 py-4 bg-red-600 hover:bg-red-700 rounded-xl font-bold shadow-lg shadow-red-900/20 flex items-center gap-2 transition-all"
              >
                <Briefcase className="w-5 h-5" /> Hire Us Now
              </button>
              
              {/* <button className="px-8 py-4 bg-slate-800 border border-slate-700 hover:bg-slate-700 rounded-xl font-bold flex items-center gap-2 transition-all">
                <Code className="w-5 h-5" /> Student Training
              </button> */}
            </div>
          </motion.div>

          {/* RIGHT SIDE: GRAPHICS (Kept exactly same) */}
          <div className="lg:w-1/2 relative h-[500px] w-full perspective-1000">
            <motion.div 
              animate="animate"
              className="absolute top-10 left-4 right-4 md:right-10 md:left-10 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-10"
            >
              <div className="bg-slate-800 px-4 py-3 flex items-center gap-2 border-b border-slate-700">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <div className="ml-4 bg-slate-900/50 px-3 py-1 rounded text-xs text-slate-500 font-mono w-full max-w-[200px] flex items-center justify-between">
                  <span>pit-institute.com</span>
                  <ExternalLink size={10} />
                </div>
              </div>
              <div className="bg-[#0B0F19] relative min-h-[300px] flex flex-col">
                 <div className="h-12 border-b border-slate-800 flex items-center justify-between px-6">
                    <div className="flex items-center gap-2">
                       <div className="w-6 h-6 rounded bg-red-600 flex items-center justify-center"><Zap size={14} className="text-white fill-current"/></div>
                       <div className="w-16 h-3 bg-slate-700 rounded-full opacity-50"></div>
                    </div>
                    <div className="flex gap-3">
                       <div className="w-12 h-2 bg-slate-700 rounded-full opacity-30"></div>
                       <div className="w-12 h-2 bg-slate-700 rounded-full opacity-30"></div>
                    </div>
                 </div>
                 <div className="flex-1 p-8 text-center flex flex-col items-center justify-center space-y-4">
                    <div className="w-3/4 h-6 bg-gradient-to-r from-red-500/80 to-orange-500/80 rounded-lg animate-pulse"></div>
                    <div className="w-1/2 h-6 bg-gradient-to-r from-orange-500/80 to-red-500/80 rounded-lg animate-pulse delay-75"></div>
                    <div className="w-2/3 h-3 bg-slate-700 rounded mt-4 opacity-50"></div>
                    <div className="flex gap-3 mt-4">
                       <div className="w-24 h-8 bg-red-600 rounded-md shadow-lg shadow-red-900/20"></div>
                       <div className="w-24 h-8 border border-slate-600 rounded-md"></div>
                    </div>
                 </div>
              </div>
            </motion.div>
            <motion.div 
              animate={{ y: [0, -10, 0], transition: { delay: 1, duration: 5, repeat: Infinity, ease: "easeInOut" } }}
              className="absolute -bottom-4 -left-2 md:left-0 w-32 md:w-36 bg-[#121212] rounded-[2rem] border-[5px] border-slate-800 shadow-2xl z-20 overflow-hidden"
            >
              <div className="absolute top-2 left-1/2 -translate-x-1/2 h-3 w-10 bg-black rounded-full z-30" />
              <div className="h-full bg-[#0B0F19] pt-8 px-3 pb-3 flex flex-col">
                 <div className="flex justify-between items-center mb-4">
                    <div className="w-5 h-5 rounded bg-red-600 flex items-center justify-center"><Zap size={10} className="text-white fill-current"/></div>
                    <Menu size={12} className="text-slate-500"/>
                 </div>
                 <div className="space-y-2 mb-4">
                    <div className="w-full h-16 bg-gradient-to-br from-red-900/40 to-slate-800 rounded-lg border border-red-500/10 p-2">
                       <div className="w-3/4 h-2 bg-red-500/50 rounded mb-1"></div>
                       <div className="w-1/2 h-2 bg-red-500/30 rounded"></div>
                    </div>
                 </div>
                 <div className="space-y-2 flex-1">
                    <div className="h-10 w-full bg-slate-800 rounded border border-slate-700"></div>
                 </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1, y: [0, -10, 0] }}
              transition={{ delay: 0.5, duration: 4, repeat: Infinity }}
              className="absolute bottom-10 right-4 md:right-10 bg-[#1E1E1E] text-blue-300 p-4 rounded-xl shadow-2xl border border-slate-700 font-mono text-[10px] md:text-xs z-20"
            >
              <div className="flex gap-1.5 mb-2 opacity-50 border-b border-white/10 pb-2">
                 <div className="text-slate-400">Backend.ts</div>
              </div>
              <div><span className="text-pink-400">const</span> <span className="text-blue-400">submit</span> = <span className="text-yellow-300">async</span>() =&gt; &#123;</div>
              <div className="pl-4"><span className="text-purple-400">await</span> API.post(<span className="text-green-400">"/quote"</span>);</div>
              <div className="pl-4"><span className="text-white">return</span> <span className="text-orange-300">true</span>;</div>
              <div>&#125;</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= PORTFOLIO SECTION ================= */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 font-aligran">Our Recent Projects</h2>
            <div className="w-24 h-1.5 bg-red-600 mx-auto rounded-full"/>
            <p className="text-slate-600 mt-6 max-w-2xl mx-auto text-lg">
              We have built high-performance platforms for various industries. Check out our live work.
            </p>
          </div>

          <motion.div 
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {/* Project 1 */}
            <motion.div variants={fadeIn} className="group bg-slate-50 rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl transition-all">
              <div className="h-56 bg-emerald-900 relative overflow-hidden flex items-center justify-center">
                 <Globe className="w-16 h-16 text-emerald-400 opacity-50 group-hover:scale-110 transition-transform"/>
                 <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button className="bg-white text-slate-900 px-6 py-2 rounded-full font-bold flex items-center gap-2 hover:bg-emerald-50 transition-colors">
                       Visit Site <ExternalLink className="w-4 h-4"/>
                    </button>
                 </div>
              </div>
              <div className="p-6">
                 <h3 className="text-xl font-bold text-slate-900 mb-2">Ayushvatika.com</h3>
                 <p className="text-slate-600 text-sm mb-4">A complete Herbal E-commerce store with SuperAdmin dashboard.</p>
                 <div className="flex gap-2 flex-wrap">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-bold">React</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-bold">Node.js</span>
                 </div>
              </div>
            </motion.div>

            {/* Project 2 */}
            <motion.div variants={fadeIn} className="group bg-slate-50 rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl transition-all">
              <div className="h-56 bg-blue-900 relative overflow-hidden flex items-center justify-center">
                 <BarChart className="w-16 h-16 text-blue-400 opacity-50 group-hover:scale-110 transition-transform"/>
                 <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button className="bg-white text-slate-900 px-6 py-2 rounded-full font-bold flex items-center gap-2 hover:bg-blue-50 transition-colors">
                       View Case Study <ExternalLink className="w-4 h-4"/>
                    </button>
                 </div>
              </div>
              <div className="p-6">
                 <h3 className="text-xl font-bold text-slate-900 mb-2">Trady Frog (B2B)</h3>
                 <p className="text-slate-600 text-sm mb-4">A massive B2B platform similar to IndiaMart. Dual-backend architecture.</p>
                 <div className="flex gap-2 flex-wrap">
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded font-bold">Spring Boot</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-bold">React TS</span>
                 </div>
              </div>
            </motion.div>

             {/* Project 3 */}
             <motion.div variants={fadeIn} className="group bg-slate-50 rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl transition-all">
              <div className="h-56 bg-purple-900 relative overflow-hidden flex items-center justify-center">
                 <Layers className="w-16 h-16 text-purple-400 opacity-50 group-hover:scale-110 transition-transform"/>
                 <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button className="bg-white text-slate-900 px-6 py-2 rounded-full font-bold flex items-center gap-2 hover:bg-purple-50 transition-colors">
                       Live Demo <ExternalLink className="w-4 h-4"/>
                    </button>
                 </div>
              </div>
              <div className="p-6">
                 <h3 className="text-xl font-bold text-slate-900 mb-2">Luxuriya Homes</h3>
                 <p className="text-slate-600 text-sm mb-4">High-end Interior Design portfolio with smooth animations.</p>
                 <div className="flex gap-2 flex-wrap">
                    <span className="px-2 py-1 bg-pink-100 text-pink-700 text-xs rounded font-bold">Framer</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-bold">Tailwind</span>
                 </div>
              </div>
            </motion.div>

          </motion.div>
        </div>
      </section>

      {/* ================= SERVICES / TECH STACK SECTION ================= */}
      <section className="py-20 px-6 bg-slate-900 text-white">
        <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="space-y-8">
                    <h2 className="text-5xl font-bold font-aligran">Why Companies Choose PIT?</h2>
                    <p className="text-slate-400 text-lg leading-relaxed">
                      We don't just use templates. We code custom solutions tailored to your business logic using the most robust technologies available.
                    </p>
                    <div className="space-y-6">
                        <div className="flex gap-4 group">
                            <div className="w-14 h-14 rounded-xl bg-red-600/10 border border-red-600/20 flex items-center justify-center text-red-500 shrink-0 group-hover:bg-red-600 group-hover:text-white transition-colors">
                                <Rocket className="w-7 h-7"/>
                            </div>
                            <div>
                                <h4 className="text-xl font-bold text-slate-100">High Speed Performance</h4>
                                <p className="text-slate-500 text-sm mt-1">Optimized for Core Web Vitals and fast loading.</p>
                            </div>
                        </div>
                        <div className="flex gap-4 group">
                            <div className="w-14 h-14 rounded-xl bg-blue-600/10 border border-blue-600/20 flex items-center justify-center text-blue-500 shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                <Monitor className="w-7 h-7"/>
                            </div>
                            <div>
                                <h4 className="text-xl font-bold text-slate-100">Admin Panels & Dashboards</h4>
                                <p className="text-slate-500 text-sm mt-1">Get full control of your data with our custom-built Admin systems.</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700 shadow-2xl">
                    <h3 className="text-3xl font-bold mb-8 text-center font-aligran">Our Technology Stack</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-5 bg-slate-900 rounded-xl border border-slate-700 flex items-center gap-3 hover:border-blue-500 transition-colors">
                            <Code className="text-blue-400 w-8 h-8"/> 
                            <div className="flex flex-col"><span className="font-bold">React.js</span><span className="text-xs text-slate-500">Frontend</span></div>
                        </div>
                        <div className="p-5 bg-slate-900 rounded-xl border border-slate-700 flex items-center gap-3 hover:border-green-500 transition-colors">
                            <Server className="text-green-400 w-8 h-8"/> 
                            <div className="flex flex-col"><span className="font-bold">Node.js</span><span className="text-xs text-slate-500">Backend</span></div>
                        </div>
                        <div className="p-5 bg-slate-900 rounded-xl border border-slate-700 flex items-center gap-3 hover:border-orange-500 transition-colors">
                            <Cpu className="text-orange-400 w-8 h-8"/> 
                            <div className="flex flex-col"><span className="font-bold">Spring Boot</span><span className="text-xs text-slate-500">Enterprise</span></div>
                        </div>
                        <div className="p-5 bg-slate-900 rounded-xl border border-slate-700 flex items-center gap-3 hover:border-yellow-500 transition-colors">
                            <Database className="text-yellow-400 w-8 h-8"/> 
                            <div className="flex flex-col"><span className="font-bold">MySQL</span><span className="text-xs text-slate-500">Database</span></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* ================= PROCESS SECTION ================= */}
      <section className="py-24 px-6 bg-slate-50">
        <div className="container mx-auto max-w-5xl text-center">
           <h2 className="text-4xl font-bold text-slate-900 mb-16 font-aligran">How We Build Your Website</h2>
           <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Requirement", desc: "We discuss your business needs and draft a plan." },
                { step: "02", title: "UI/UX Design", desc: "We create modern, attractive designs for approval." },
                { step: "03", title: "Development", desc: "Our experts code the frontend and backend logic." },
                { step: "04", title: "Deployment", desc: "We host it on VPS (Ubuntu/Nginx) with SSL." }
              ].map((item, idx) => (
                  <div key={idx} className="relative group p-4">
                      <div className="text-7xl font-black text-slate-200 absolute -top-4 left-1/2 -translate-x-1/2 -z-10 group-hover:text-red-100 transition-colors duration-300">
                          {item.step}
                      </div>
                      <div className="w-16 h-16 mx-auto bg-white rounded-full shadow-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                         {idx === 0 && <Briefcase className="text-red-500"/>}
                         {idx === 1 && <Layers className="text-red-500"/>}
                         {idx === 2 && <Code className="text-red-500"/>}
                         {idx === 3 && <Rocket className="text-red-500"/>}
                      </div>
                      <h4 className="text-xl font-bold text-slate-900">{item.title}</h4>
                      <p className="text-slate-600 text-sm mt-2 leading-relaxed">{item.desc}</p>
                  </div>
              ))}
           </div>
        </div>
      </section>

      {/* ================= UPDATED COMPACT WHITE FORM (MATCHING APP PAGE) ================= */}
      <AnimatePresence>
        {isFormOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsFormOpen(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />

            {/* Modal Content - Compact & Clean */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white border border-slate-100 w-full max-w-md rounded-2xl shadow-2xl relative z-10 overflow-hidden"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-red-600"/> Web Project Inquiry
                  </h3>
                  <p className="text-xs text-slate-500">Get a free quote within 24 hours.</p>
                </div>
                <button 
                  onClick={() => setIsFormOpen(false)}
                  className="text-slate-400 hover:text-red-500 p-1 rounded-full hover:bg-slate-100 transition"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Form Body */}
              <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Your Name</label>
                    <input 
                      type="text" 
                      name="name" 
                      value={formData.name} 
                      onChange={handleInputChange} 
                      placeholder="John Doe" 
                      required
                      className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Phone</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleInputChange} 
                      placeholder="+91..." 
                      required
                      className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Company (Optional)</label>
                  <input 
                    type="text" 
                    name="companyName" 
                    value={formData.companyName} 
                    onChange={handleInputChange} 
                    placeholder="Your Company Ltd." 
                    className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Email</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleInputChange} 
                    placeholder="john@example.com" 
                    required
                    className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Idea Brief</label>
                  <textarea 
                    rows={3}
                    name="projectBrief" 
                    value={formData.projectBrief} 
                    onChange={handleInputChange} 
                    placeholder="Tell us briefly what you want to build..." 
                    required
                    className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all resize-none"
                  ></textarea>
                </div>

                <div className="pt-2">
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold py-3 rounded-lg shadow-lg shadow-red-900/30 transition-all flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"/>
                    ) : (
                      <>Submit Request <Send size={16} /></>
                    )}
                  </button>
                </div>
              </form>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <SuccessPopup isOpen={showSuccess} onClose={() => setShowSuccess(false)} />
    </div>
  );
};

export default WithWeb;